import { useState, useRef } from 'react';
import { profileService } from '../lib/profile-service';

/**
 * Image Upload Hook Options
 */
export interface UseImageUploadOptions {
  /**
   * User ID for the upload (regular user ID or guest user ID)
   */
  userId: string;
  
  /**
   * Whether this is a guest user upload
   */
  isGuest?: boolean;
  
  /**
   * Maximum file size in bytes (default: 5MB)
   */
  maxSizeBytes?: number;
  
  /**
   * Allowed file types (default: any image)
   */
  allowedTypes?: string[];
  
  /**
   * Callback fired when upload completes successfully
   */
  onSuccess?: (imageUrl: string) => void;
  
  /**
   * Callback fired when upload fails
   */
  onError?: (error: string) => void;
}

/**
 * Image Upload Hook Return Value
 */
export interface UseImageUploadReturn {
  /**
   * Whether an upload is currently in progress
   */
  uploading: boolean;
  
  /**
   * Error message if upload failed
   */
  error: string;
  
  /**
   * Currently uploaded image URL
   */
  imageUrl: string;
  
  /**
   * Reference to the hidden file input element
   */
  fileInputRef: React.RefObject<HTMLInputElement>;
  
  /**
   * Trigger the file picker dialog
   */
  triggerFilePicker: () => void;
  
  /**
   * Handle file selection from input
   */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  
  /**
   * Manually upload a file
   */
  uploadFile: (file: File) => Promise<string | undefined>;
  
  /**
   * Clear the error message
   */
  clearError: () => void;
  
  /**
   * Set the image URL manually (for initial values)
   */
  setImageUrl: (url: string) => void;
}

const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_ALLOWED_TYPES = ['image/'];

/**
 * Reusable Image Upload Hook
 * 
 * Provides image upload functionality with validation, progress tracking,
 * and error handling. Can be used for profile images, guest writer images, etc.
 * 
 * @example
 * ```tsx
 * const { uploading, error, imageUrl, fileInputRef, triggerFilePicker, handleFileChange } = useImageUpload({
 *   userId: user.id,
 *   isGuest: false,
 *   onSuccess: (url) => console.log('Uploaded:', url),
 * });
 * 
 * return (
 *   <>
 *     <input
 *       ref={fileInputRef}
 *       type="file"
 *       accept="image/*"
 *       onChange={handleFileChange}
 *       className="hidden"
 *     />
 *     <button onClick={triggerFilePicker} disabled={uploading}>
 *       {uploading ? 'Uploading...' : 'Upload Image'}
 *     </button>
 *   </>
 * );
 * ```
 */
export function useImageUpload(options: UseImageUploadOptions): UseImageUploadReturn {
  const {
    userId,
    isGuest = false,
    maxSizeBytes = DEFAULT_MAX_SIZE,
    allowedTypes = DEFAULT_ALLOWED_TYPES,
    onSuccess,
    onError,
  } = options;
  
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  /**
   * Validate file before upload
   */
  const validateFile = (file: File): string | null => {
    // Check file type
    const isValidType = allowedTypes.some(type => file.type.startsWith(type));
    if (!isValidType) {
      return 'Please select a valid image file.';
    }
    
    // Check file size
    if (file.size > maxSizeBytes) {
      const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
      return `Image size must be less than ${maxSizeMB}MB.`;
    }
    
    return null;
  };
  
  /**
   * Upload file to storage
   */
  const uploadFile = async (file: File): Promise<string | undefined> => {
    setUploading(true);
    setError('');
    
    try {
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        onError?.(validationError);
        return undefined;
      }
      
      // Upload based on user type
      let uploadedUrl: string;
      if (isGuest) {
        uploadedUrl = await profileService.uploadGuestWriterImage(userId, file);
      } else {
        uploadedUrl = await profileService.uploadProfileImage(userId, file);
      }
      
      setImageUrl(uploadedUrl);
      onSuccess?.(uploadedUrl);
      
      return uploadedUrl;
    } catch (err) {
      const errorMessage = 'Failed to upload image. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
      return undefined;
    } finally {
      setUploading(false);
    }
  };
  
  /**
   * Handle file input change
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };
  
  /**
   * Trigger the file picker
   */
  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };
  
  /**
   * Clear error message
   */
  const clearError = () => {
    setError('');
  };
  
  return {
    uploading,
    error,
    imageUrl,
    fileInputRef,
    triggerFilePicker,
    handleFileChange,
    uploadFile,
    clearError,
    setImageUrl,
  };
}

